/**
 * Organizations Repository
 *
 * Manages organizations with full CRUD + members management.
 * Follows the repository pattern for clean Redux integration.
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { get, post, put, del } from 'data/services/apiService';

// ============================================
// Create Repository
// ============================================
const organizationsRepository = createRepository({
	endpoint: 'organizations',
	name: 'organizations',
	statePath: 'organizations',

	customMethods: {
		/**
		 * Get current active organization
		 * Usage: actions.getActive()
		 */
		getActive: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/organizations/active`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Switch to a different organization
		 * Usage: actions.switchOrganization({ id: 5 })
		 */
		switchOrganization: ({ id, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/organizations/${id}/switch`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get organization statistics
		 * Usage: actions.getStats({ id: 5 })
		 */
		getStats: ({ id, authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/organizations/${id}/stats`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get organization members
		 * Usage: actions.getMembers({ id: 5 })
		 */
		getMembers: ({ id, authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/organizations/${id}/members`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Add member to organization
		 * Usage: actions.addMember({ id: 5, data: { email, role } })
		 */
		addMember: ({ id, data, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/organizations/${id}/members`,
				data,
				authToken,
				dispatch,
			});
		},

		/**
		 * Update member role
		 * Usage: actions.updateMember({ id: 5, userId: 10, data: { role } })
		 */
		updateMember: ({ id, userId, data, authToken, dispatch }) => {
			return put({
				url: `${getApiUrl()}/api/organizations/${id}/members/${userId}`,
				data,
				authToken,
				dispatch,
			});
		},

		/**
		 * Remove member from organization
		 * Usage: actions.removeMember({ id: 5, userId: 10 })
		 */
		removeMember: ({ id, userId, authToken, dispatch }) => {
			return del({
				url: `${getApiUrl()}/api/organizations/${id}/members/${userId}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Leave organization (self-remove)
		 * Usage: actions.leaveOrganization({ id: 5 })
		 */
		leaveOrganization: ({ id, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/organizations/${id}/leave`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Transfer organization ownership
		 * Usage: actions.transferOwnership({ id: 5, data: { new_owner_id } })
		 */
		transferOwnership: ({ id, data, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/organizations/${id}/transfer-ownership`,
				data,
				authToken,
				dispatch,
			});
		},
	},
});

// ============================================
// Exports
// ============================================

export const {
	name,
	statePath,
	thunks: organizationsThunks,
	reducer: organizationsReducer,
	actions: organizationsActions,
	types: organizationsTypes,
	selectors: organizationsSelectors,
	useHook: useOrganizations,
} = organizationsRepository;

// Export repository for advanced usage
export default organizationsRepository;
